import { useEffect, useRef, useState } from "react";
import type { TouchEvent } from "react";
import { Link } from "react-router-dom";
import type { Title } from "@/types/media";
import { CATEGORY_META } from "@/lib/media";

interface HeroCarouselProps {
  slides: Title[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5500);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + slides.length) % slides.length);
  };

  const onTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full h-[68vh] min-h-[460px] md:h-[640px] overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="焦点内容轮播"
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.banner}
            alt={`${slide.title} 焦点海报`}
            title={`${slide.title} 高清在线观看`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/55 to-background-950/35"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/85 via-background-950/25 to-transparent"></div>

          <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-4 animate-fade-up">
                <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-semibold">
                  {CATEGORY_META[slide.type].label}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-background-950/70 border border-background-700 text-foreground-100 text-xs">
                  {slide.year}
                </span>
                <span className="flex items-center gap-1 text-accent-400 text-sm font-semibold">
                  <i className="ri-star-fill"></i>
                  {slide.rating.toFixed(1)}
                </span>
              </div>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-50 leading-tight mb-4 animate-fade-up">
                {slide.title}
              </h2>
              <p className="text-sm md:text-base text-foreground-200 leading-relaxed mb-6 line-clamp-2 max-w-xl animate-fade-up">
                {slide.synopsis}
              </p>
              <div className="flex flex-wrap items-center gap-3 animate-fade-up">
                <Link
                  to={`/detail/${slide.id}`}
                  className="flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
                >
                  <i className="ri-play-fill text-lg"></i>
                  立即观看
                </Link>
                <Link
                  to={`/detail/${slide.id}`}
                  className="flex items-center gap-2 px-6 py-3 rounded-md bg-background-950/60 border border-background-600 hover:border-primary-500 text-foreground-100 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
                >
                  <i className="ri-information-line text-lg"></i>
                  查看详情
                </Link>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => go(-1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-background-950/60 border border-background-700 hover:bg-primary-500 hover:border-primary-500 items-center justify-center text-foreground-100 cursor-pointer transition-colors"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-background-950/60 border border-background-700 hover:bg-primary-500 hover:border-primary-500 items-center justify-center text-foreground-100 cursor-pointer transition-colors"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-7 bg-primary-500" : "w-3 bg-foreground-50/40 hover:bg-foreground-50/70"
            }`}
            aria-label={`切换到第 ${i + 1} 张`}
          />
        ))}
      </div>
    </section>
  );
}